import React, { useEffect, useState } from 'react';
import ComplianceSection from './ComplianceSection';
import ViewStaffComplianceForm from '../common/ViewStaffComplianceForm';
import ComplianceSelectBox from '../common/ComplianceSelectBox';
import { IUpComingStaffCompliance } from '../../type/upComingStaffCompliance';
import complianceApi from '../../api/complianceApi';
import { IUpdateStaffInStaffCompliance } from '../../type/updateStaff';
import ViewStaffComplianceOnBoarding from './ViewStaffComplianceCardOnBoarding';
import { COMPLIANCE_STATUS } from '../../constants/constant';
import ReusableSelect from '../common/Select';
import { IComplianceStaff } from '../../type/complianceStaff';
import useLocationStore from '../../store/locatonStore';
import { toast } from 'react-toastify';

type Props = {
  data: IUpComingStaffCompliance[];
  userCanEditStaffCompliance?: boolean;
  onStaffChange?: () => void;
  driverAccordionIsOpen?: boolean;
  toggleState?: (isOpen: boolean) => void;
  onSubmitSuccess?: () => void;
  userHavePermissionToUpdateStatusStaffCompliance?: boolean;
  userHavePermissionToReadStatusStaffCompliance?: boolean;
};

const StaffComplianceSection: React.FC<Props> = ({
  data,
  onStaffChange,
  driverAccordionIsOpen,
  toggleState,
  onSubmitSuccess,
  userCanEditStaffCompliance,
  userHavePermissionToReadStatusStaffCompliance,
}) => {
  const [isEditMode, setEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCompliance, setSelectedCompliance] =
    useState<IUpComingStaffCompliance | null>(null);

  const [updatedAllocations, setUpdatedAllocations] = useState<{
    [key: number]: number;
  }>({});

  const handleStatusClick = (item: IUpComingStaffCompliance) => {
    setSelectedCompliance(item);
    setShowModal(true);
  };

  const staffHeaders = [
    { label: 'Staff Name' },
    { label: 'Compliance' },
    { label: 'Category' },
    { label: 'Status' },
    { label: 'Allocated' },
  ];

  const allocatedStaff = data.reduce(
    (acc, item, idx) => {
      acc[idx] = item.allocatedStaffId;
      return acc;
    },
    {} as { [key: number]: number },
  );
  const selectedLocation = useLocationStore((state) => state.selectedLocation);
  const handleAllocate = (index: number, staff: any, item: any) => {
    if (!staff.staffId) return;
    setUpdatedAllocations((prev) => ({
      ...prev,
      [index]: staff?.staffId,
    }));
  };
  const [apiResponse, setApiResponse] = useState<IComplianceStaff[]>([]);

  useEffect(() => {
    fetchStaffList();
  }, [selectedLocation]);

  const fetchStaffList = async () => {
    const response = await complianceApi.getStaffList(selectedLocation.OwnerId);
    const staffList: IComplianceStaff[] = response?.data || [];
    setApiResponse(staffList);
  };

  const staffRows = data.map((item, index) => {
    const statusColor =
      item.upcomingDays <= 7
        ? 'text-red-500'
        : item.upcomingDays <= 30
          ? 'text-yellow-500'
          : 'text-gray-700';

    return [
      item.staffName,
      item.complianceDescription,
      item.complianceCategoryStatus,
      <td
        key={index}
        className={`font-semibold ${userHavePermissionToReadStatusStaffCompliance ? 'cursor-pointer underline' : ''} ${statusColor} text-text-textBlackColor`}
        onClick={() => handleStatusClick(item)}
      >
        {item?.status === COMPLIANCE_STATUS.onBoarding
          ? item.status
          : item?.displayText}
      </td>,
      isEditMode ? (
        <ReusableSelect
          options={apiResponse}
          getOptionLabel={(option) => option.staffName}
          getOptionValue={(option) => option.staffId}
          onChange={(selectedStaff) =>
            handleAllocate(index, selectedStaff, item)
          }
          placeholder="Select"
          className="w-full p-1 bg-sidebar-background text-text-primary"
          value={updatedAllocations[index] ?? item?.allocatedStaffId}
          isDisabled={!isEditMode}
        />
      ) : (
        <span className="text-textBlack">
          {apiResponse.find((opt) => opt.staffId === item?.allocatedStaffId)
            ?.staffName || 'Unassigned'}
        </span>
      ),
    ];
  });

  const handleEdit = () => setEditMode((prev) => !prev);
  const handleCancel = () => setEditMode(false);
  const handleSave = () => {
    const changedAllocations: IUpdateStaffInStaffCompliance[] = Object.entries(
      updatedAllocations,
    ).map(([indexStr, newStaffId]) => {
      const index = parseInt(indexStr, 10);
      const item = data[index];
      return {
        complianceId: item?.complianceId,
        allocStaffId: newStaffId,
        ownerId: selectedLocation?.OwnerId,
        staffId: item.staffId,
      };
    });

    console.log('Changed Allocations:', changedAllocations);
    updateStaffInStaffCompliance(changedAllocations);
    setEditMode(false);
  };

  const updateStaffInStaffCompliance = async (
    data: IUpdateStaffInStaffCompliance[],
  ) => {
    try {
      const response = await complianceApi.updateStaffInStaffCompliance(data);
      console.log(response);
      if (onStaffChange) {
        onStaffChange();
      }
      toast.success('Details updated Successfully');
    } catch (error) {
      toast.success('Error occurs');
      console.log(error);
    }
  };

  return (
    <>
      <div className="ml-6 mr-4">
        <ComplianceSection
          title="Upcoming Staff Compliances"
          headers={staffHeaders}
          rows={staffRows}
          allocatedStaff={allocatedStaff}
          updatedAllocations={updatedAllocations}
          isEditMode={isEditMode}
          isEdit={false}
          onEditClick={handleEdit}
          onCancel={handleCancel}
          onSave={handleSave}
          userCanEditStaffCompliance={userCanEditStaffCompliance}
          driverAccordionIsOpen={driverAccordionIsOpen}
          toggleState={toggleState}
        />
        {showModal &&
          selectedCompliance &&
          userHavePermissionToReadStatusStaffCompliance && (
            <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center px-4">
              {selectedCompliance.status === COMPLIANCE_STATUS.onBoarding ? (
                <ViewStaffComplianceOnBoarding
                  name={selectedCompliance.staffName}
                  compliance={selectedCompliance.complianceDescription}
                  category={selectedCompliance.status}
                  recurrency={selectedCompliance?.concurrencyDescription}
                  status={selectedCompliance.status}
                  complianceLink={selectedCompliance.staffExternalLink}
                  closeModal={() => setShowModal(false)}
                  staffId={selectedCompliance?.staffId}
                  complianceId={selectedCompliance.complianceId}
                  note={selectedCompliance?.note}
                  nextComplianceDate={selectedCompliance?.nextComplianceDate}
                  lastComplianceDateDisplay={
                    selectedCompliance?.lastComplianceDate
                  }
                />
              ) : (
                <ViewStaffComplianceForm
                  name={selectedCompliance.staffName}
                  compliance={selectedCompliance.complianceDescription}
                  category={selectedCompliance.status}
                  recurrency={selectedCompliance?.concurrencyDescription}
                  status={selectedCompliance.displayText}
                  complianceLink={selectedCompliance.staffExternalLink}
                  closeModal={() => setShowModal(false)}
                  staffId={selectedCompliance?.staffId}
                  complianceId={selectedCompliance.complianceId}
                  note={selectedCompliance?.note}
                  nextComplianceDate={selectedCompliance?.nextComplianceDate}
                  lastComplianceDateDisplay={
                    selectedCompliance?.lastComplianceDate
                  }
                  isUserCanEditStaffCompliance={
                    selectedCompliance?.isUserCanEditStaffCompliance
                  }
                  onStaffChange={onStaffChange}
                  onSubmitSuccess={onSubmitSuccess}
                />
              )}
            </div>
          )}
      </div>
    </>
  );
};

export default StaffComplianceSection;
